/**
 * NAD Collection Product Grid Section
 * Handles collection product grid with CSS Grid layout and animations
 */

export class NADCollectionProductGrid {
  private section: HTMLElement;

  constructor(section: HTMLElement) {
    this.section = section;
    this.init();
  }

  private init(): void {
    // Check if already initialized
    if (this.section.dataset["initialized"] === "true") {
      return;
    }

    this.setupAnimation();
    this.section.dataset["initialized"] = "true";
  }

  /**
   * Setup section animations
   */
  private setupAnimation(): void {
    if (!this.section) return;

    // Check if IntersectionObserver is available
    if (!('IntersectionObserver' in window)) {
      // Fallback: animate immediately
      this.animateSection();
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px 0px -20% 0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.animateSection();
            observer.unobserve(entry.target);
            console.log('NADCollectionProductGrid: Section became visible', this.section.id);
          }
        });
      },
      options
    );

    observer.observe(this.section);
  }

  /**
   * Animate section with staggered elements
   */
  private animateSection(): void {
    // Animate main section
    this.section.classList.add('animate-in');
    
    // Animate collection grid with delay
    const collectionGrid = this.section.querySelector('.nad-collection-grid');
    if (collectionGrid) {
      setTimeout(() => {
        collectionGrid.classList.add('animate-in');
        console.log('NADCollectionProductGrid: Collection grid animated');
      }, 200);
    }
    
    // Animate product cards with stagger
    const productWrappers = this.section.querySelectorAll('.nad-collection-grid__product-wrapper');
    productWrappers.forEach((wrapper, index) => {
      setTimeout(() => {
        wrapper.classList.add('animate-in');
      }, 400 + (index * 100)); // Start after collection grid, then stagger
    });

    console.log('NADCollectionProductGrid: Section animation started with', productWrappers.length, 'product cards');
  }


  public destroy(): void {
    this.section.dataset["initialized"] = "false";
  }

  // Static method to initialize all sections
  static initializeAll(): void {
    const sections = document.querySelectorAll<HTMLElement>(".nad-collection-product-grid");
    
    sections.forEach((section) => {
      // Attach instance to DOM node for later reference
      if (!(section as any)._nadCollectionProductGridInstance) {
        (section as any)._nadCollectionProductGridInstance = new NADCollectionProductGrid(section);
      }
    });
  }

  // Static method to destroy a section instance
  static destroySection(section: HTMLElement): void {
    const instance = (section as any)._nadCollectionProductGridInstance;
    if (instance && typeof instance.destroy === "function") {
      instance.destroy();
      delete (section as any)._nadCollectionProductGridInstance;
    }
    section.dataset["initialized"] = "false";
  }
}
